import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { getTag } from '../graphql/queries'
import { updateTag, deleteTag } from '../graphql/mutation'
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import ProgressBar from '../components/circularProgress/circularProgress'

export default function TagPage() {
  const { id } = useParams()
  const client = useApolloClient()
  const navigate = useNavigate()

  const [tag, setTag] = useState({
    name: '',
    id: '',
    isDefault: false,
    createdAt: '',
    updatedAt: ''
  })

  const [tagData, setTagData] = useState(null)
  const [progress, setProgress] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { loading, error, data } = useQuery(getTag, {
    variables: { tagId: id },
    onCompleted: data => {
      setTagData(data.getTag)
    },
  })

  useEffect(() => {
    if (tagData) {
      setTag({
        name: tagData.name,
        id: tagData.id,
        isDefault: tagData.isDefault || false,
        createdAt: tagData.createdAt,
        updatedAt: tagData.updatedAt
      })
    }
  }, [tagData])

  const [updateTagMutation] = useMutation(updateTag)
  const [deleteTagMutation] = useMutation(deleteTag)

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const handleUpdateTag = async () => {
    if (!tag.name.trim()) {
      setSnackBarMessage('Tag name is required')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      const response = await updateTagMutation({
        variables: {
          tagId: tag.id,
          name: tag.name.trim(),
          isDefault: tag.isDefault
        }
      })

      if (response.data) {
        setSnackBarMessage('Tag updated successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Update cache
        client.writeQuery({
          query: getTag,
          variables: { tagId: id },
          data: {
            getTag: {
              ...response.data.updateTag,
              __typename: 'Tag'
            }
          }
        })
      }
    } catch (error) {
      console.error('Error updating tag:', error)
      setSnackBarMessage(`Error updating tag: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleDeleteTag = async () => {
    setDeleteProgress(true)
    try {
      const response = await deleteTagMutation({
        variables: {
          tagId: tag.id
        }
      })

      if (response.data) {
        setSnackBarMessage('Tag deleted successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Navigate back to tags list after a short delay
        setTimeout(() => {
          navigate('/tags')
        }, 1500)
      }
    } catch (error) {
      console.error('Error deleting tag:', error)
      setSnackBarMessage(`Error deleting tag: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setDeleteProgress(false)
      setDeleteDialogOpen(false)
    }
  }

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setTag(prev => ({ ...prev, [name]: value }))
  }

  const handleToggleChange = (event, newValue) => {
    if (newValue !== null) {
      setTag((prev) => ({ ...prev, isDefault: newValue }))
    }
  }

  if (loading) return <ProgressBar />
  if (error) return <Typography color="error">Error loading tag: {error.message}</Typography>

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 4, 
      maxWidth: '800px', 
      mx: 'auto', 
      px: 2, 
      py: 2 
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <LocalOfferIcon fontSize="large" sx={{ color: '#1976d2' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Tag Details
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Tag Name */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Tag Name
            </Typography>
            <TextField
              fullWidth
              name="name"
              value={tag.name}
              onChange={handleChange}
              placeholder="Enter tag name..."
              variant="outlined"
              sx={{ mb: 1 }}
            />
          </Box>

          {/* Is Default */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Is Default Tag
            </Typography>
            <ToggleButtonGroup
              value={tag.isDefault}
              exclusive
              onChange={handleToggleChange}
              aria-label="default tag toggle"
              fullWidth
              sx={{ mb: 1 }}
            >
              <ToggleButton 
                value={false} 
                aria-label="not default"
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#bbdefb'
                    }
                  }
                }}
              >
                No
              </ToggleButton>
              <ToggleButton 
                value={true} 
                aria-label="default"
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: '#e8f5e8',
                    color: '#2e7d32',
                    '&:hover': {
                      backgroundColor: '#c8e6c9'
                    }
                  }
                }}
              >
                Yes
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Toggle whether this tag should be a default tag
            </Typography>
          </Box>

          <Divider />

          {/* Tag Information */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Tag Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Tag ID:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', px: 2, py: 1, borderRadius: 1 }}>
                  {tag.id}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Created At:
                </Typography>
                <Typography variant="body2">
                  {tag.createdAt ? new Date(tag.createdAt).toLocaleString('tr-TR') : '-'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Updated At:
                </Typography>
                <Typography variant="body2">
                  {tag.updatedAt ? new Date(tag.updatedAt).toLocaleString('tr-TR') : '-'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={deleteProgress ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              onClick={handleDeleteClick}
              disabled={deleteProgress}
              sx={{ 
                minWidth: 120,
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#c62828',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              {deleteProgress ? 'Deleting...' : 'Delete Tag'}
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/tags')}
                sx={{ minWidth: 100 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                startIcon={progress ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleUpdateTag}
                disabled={progress}
                sx={{ 
                  minWidth: 120,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                {progress ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={handleSnackBarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackBarClose} 
          severity={snackBarSeverity}
          sx={{ width: '100%' }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Tag
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the tag "{tag.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTag} 
            color="error" 
            variant="contained"
            disabled={deleteProgress}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
} 